import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/layouts/RootLayout';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const MATERIALS = ['Cedar', 'Vinyl', 'Chain Link', 'Aluminum', 'Other'];
const HEIGHTS = ['4 ft', '5 ft', '6 ft', '8 ft', 'Custom'];

const FAQ = [
  ['What should a fence estimate scope include?', 'A useful scope should identify the fence type, approximate footage, height, gates, removal work, major inclusions, exclusions and any site conditions or customer decisions that still affect the work.'],
  ['Is this a contract template?', 'No. This builder creates a planning and proposal-scope summary. It is not legal advice and does not replace contract terms appropriate for your business and jurisdiction.'],
  ['Does the scope builder calculate a price?', 'No. Use the Fence Estimate Calculator for selling-price math and Profit Guard for direct-cost and gross-margin review.'],
  ['Does PicketOps save what I enter here?', 'No. This public builder does not create or update customer, estimate, proposal or job records. Copy the result or move into the authenticated workspace for saved records.'],
];

export default function FenceEstimateTemplate() {
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const incomingMaterial = MATERIALS.includes(searchParams.get('material')) ? searchParams.get('material') : 'Cedar';
  const [projectName, setProjectName] = useState('');
  const [material, setMaterial] = useState(incomingMaterial);
  const [height, setHeight] = useState('6 ft');
  const [customHeight, setCustomHeight] = useState('');
  const [feet, setFeet] = useState(searchParams.get('feet') || '');
  const [gates, setGates] = useState(searchParams.get('gates') || '0');
  const [tearout, setTearout] = useState(searchParams.get('tearout') || '0');
  const [included, setIncluded] = useState('Standard installation labor and listed fence materials.');
  const [excluded, setExcluded] = useState('Permits, surveys, grading, landscaping repair and hidden-condition work unless specifically added.');
  const [siteNotes, setSiteNotes] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [copied, setCopied] = useState(false);

  const selectedHeight = height === 'Custom' ? customHeight.trim() : height;
  const numericFeet = Number(feet);
  const numericGates = Number(gates);
  const numericTearout = Number(tearout);
  const valid = String(feet).trim() !== ''
    && Number.isFinite(numericFeet) && numericFeet > 0
    && Number.isInteger(numericGates) && numericGates >= 0
    && Number.isFinite(numericTearout) && numericTearout >= 0
    && Boolean(material)
    && Boolean(selectedHeight);

  const scopeText = useMemo(() => {
    if (!valid) return '';
    const lines = [
      projectName.trim() ? projectName.trim() : 'Fence installation scope',
      '',
      'PROJECT SCOPE',
      '- Install approximately ' + numericFeet.toLocaleString() + ' linear feet of ' + material + ' fencing at ' + selectedHeight + ' height.',
      '- Gates: ' + numericGates + '.',
      numericTearout > 0 ? '- Remove approximately ' + numericTearout.toLocaleString() + ' linear feet of existing fence.' : '- Existing-fence removal: none included unless added later.',
      '',
      'INCLUDED',
      included.trim() || 'No additional inclusions listed.',
      '',
      'EXCLUSIONS / ASSUMPTIONS',
      excluded.trim() || 'No exclusions listed.',
      '',
      'SITE / ACCESS NOTES',
      siteNotes.trim() || 'No site notes added.',
      '',
      'CUSTOMER DECISIONS / OPEN QUESTIONS',
      customerNotes.trim() || 'No open questions recorded.',
      '',
      'Review measurements, material specifications, gate locations, site conditions, pricing assumptions and contract terms before presenting the final quote.',
    ];
    return lines.join('\n');
  }, [projectName, material, selectedHeight, numericFeet, numericGates, numericTearout, included, excluded, siteNotes, customerNotes, valid]);

  const copyScope = async () => {
    if (!scopeText) return;
    try {
      await navigator.clipboard.writeText(scopeText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const clear = () => {
    setProjectName('');
    setMaterial('Cedar');
    setHeight('6 ft');
    setCustomHeight('');
    setFeet('');
    setGates('0');
    setTearout('0');
    setIncluded('Standard installation labor and listed fence materials.');
    setExcluded('Permits, surveys, grading, landscaping repair and hidden-condition work unless specifically added.');
    setSiteNotes('');
    setCustomerNotes('');
    setCopied(false);
  };

  const workspaceTarget = isAuthenticated ? '/estimates' : '/contractor/signup';

  return (
    <div className="picketops-public min-h-svh bg-background text-foreground">
      <header className="max-w-6xl mx-auto px-4 pt-20 pb-6 sm:pr-32">
        <Link to="/" aria-label="PicketOps home" className="inline-flex items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <span aria-hidden="true" className="flex size-10 items-center justify-center rounded-md bg-primary text-primary-foreground font-heading text-xl font-bold">P</span>
          <span>
            <span className="block font-heading text-xl font-bold tracking-tight">PICKETOPS</span>
            <span className="block text-xs text-muted-foreground">Revenue OS for fence contractors</span>
          </span>
        </Link>
        <nav aria-label="Breadcrumb" className="mt-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:underline">Home</Link>
          <span aria-hidden="true"> / </span>
          <Link to="/fence-contractor-tools" className="hover:underline">Free tools</Link>
          <span aria-hidden="true"> / </span>
          <span aria-current="page">Fence estimate scope builder</span>
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-4 pb-16 space-y-10">
        <section className="picketops-revenue-surface overflow-hidden rounded-2xl border border-primary/25 bg-card shadow-(--shadow-md)">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr]">
            <div className="p-6 sm:p-8 lg:p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Free scope builder · No signup required</p>
              <h1 className="mt-4 text-4xl sm:text-5xl font-heading font-bold tracking-tight leading-tight">Fence estimate template and scope-of-work builder</h1>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">
                Turn footage, fence type, gates, removal, inclusions and site notes into a structured scope you can copy into a proposal or review before creating the saved estimate.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild><a href="#scope-builder">Build the scope <ApperIcon name="ArrowDown" size={16}/></a></Button>
                <Button variant="outline" asChild><Link to="/fence-estimate-checklist">Review estimate checklist</Link></Button>
              </div>
            </div>
            <div className="border-t lg:border-t-0 lg:border-l border-border bg-muted/30 p-6 sm:p-8 lg:p-10">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">What this builder does</p>
              <div className="mt-5 space-y-3">
                {[
                  ['Ruler', 'Turns measurements into readable scope'],
                  ['Fence', 'Keeps material, height and gates visible'],
                  ['Shovel', 'Separates removal from new installation'],
                  ['ListChecks', 'Makes inclusions and exclusions explicit'],
                ].map(([icon, label]) => (
                  <div key={label} className="flex items-center gap-3 rounded-lg border border-border bg-background p-3 text-sm">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"><ApperIcon name={icon} size={16}/></span>
                    <span>{label}</span>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-xs leading-relaxed text-muted-foreground">This is a planning template, not a legal contract or substitute for site verification.</p>
            </div>
          </div>
        </section>

        <section id="scope-builder" aria-labelledby="scope-builder-heading" className="grid gap-5 scroll-mt-6 lg:grid-cols-[0.95fr_1.05fr]">
          <Card>
            <CardContent className="p-5 sm:p-6 space-y-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">Job facts</p>
                <h2 id="scope-builder-heading" className="mt-2 text-2xl font-heading font-semibold">Build the scope from what you know.</h2>
                <p className="mt-2 text-sm text-muted-foreground">Anything entered here stays in this browser session and is not saved to PicketOps.</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="scope-project">Project / customer label <span className="text-muted-foreground">(optional)</span></Label>
                <Input id="scope-project" value={projectName} onChange={event=>setProjectName(event.target.value)} placeholder="Smith backyard privacy fence"/>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="scope-material">Fence material</Label>
                  <Select value={material} onValueChange={setMaterial}>
                    <SelectTrigger id="scope-material"><SelectValue/></SelectTrigger>
                    <SelectContent>{MATERIALS.map(value=><SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="scope-height">Fence height</Label>
                  <Select value={height} onValueChange={setHeight}>
                    <SelectTrigger id="scope-height"><SelectValue/></SelectTrigger>
                    <SelectContent>{HEIGHTS.map(value=><SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {height === 'Custom' && <div className="space-y-2"><Label htmlFor="scope-custom-height">Custom height</Label><Input id="scope-custom-height" value={customHeight} onChange={event=>setCustomHeight(event.target.value)} placeholder="7 ft"/></div>}

              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2"><Label htmlFor="scope-feet">Linear feet</Label><Input id="scope-feet" type="number" min="0" step="0.01" inputMode="decimal" value={feet} onChange={event=>setFeet(event.target.value)} placeholder="180"/></div>
                <div className="space-y-2"><Label htmlFor="scope-gates">Gates</Label><Input id="scope-gates" type="number" min="0" step="1" inputMode="numeric" value={gates} onChange={event=>setGates(event.target.value)} placeholder="2"/></div>
                <div className="space-y-2"><Label htmlFor="scope-tearout">Tear-out feet</Label><Input id="scope-tearout" type="number" min="0" step="0.01" inputMode="decimal" value={tearout} onChange={event=>setTearout(event.target.value)} placeholder="80"/></div>
              </div>

              <div className="space-y-2"><Label htmlFor="scope-included">Included work</Label><Textarea id="scope-included" rows={3} value={included} onChange={event=>setIncluded(event.target.value)} placeholder="Materials, installation labor, standard cleanup..."/></div>
              <div className="space-y-2"><Label htmlFor="scope-excluded">Exclusions / assumptions</Label><Textarea id="scope-excluded" rows={3} value={excluded} onChange={event=>setExcluded(event.target.value)} placeholder="Permit, survey, grading, staining, hidden-condition work..."/></div>
              <div className="space-y-2"><Label htmlFor="scope-site">Site / access notes</Label><Textarea id="scope-site" rows={3} value={siteNotes} onChange={event=>setSiteNotes(event.target.value)} placeholder="Driveway access, grade changes, utility concerns, pets, landscaping..."/></div>
              <div className="space-y-2"><Label htmlFor="scope-customer">Customer decisions / open questions</Label><Textarea id="scope-customer" rows={3} value={customerNotes} onChange={event=>setCustomerNotes(event.target.value)} placeholder="Gate swing, stain color, final layout, start timing..."/></div>

              {!valid && (feet || customHeight || Number(gates) > 0 || Number(tearout) > 0) && <p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">Add a positive fence length, use a whole nonnegative gate count, and make sure the height is specified.</p>}

              <Button type="button" variant="ghost" onClick={clear}><ApperIcon name="RotateCcw" size={16}/>Clear builder</Button>
            </CardContent>
          </Card>

          <div className="space-y-5">
            <section className="overflow-hidden rounded-2xl border border-primary/25 bg-card shadow-(--shadow-sm)">
              <div className="border-b border-border bg-primary/5 p-5 sm:p-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">Scope preview</p>
                <h2 className="mt-2 text-2xl font-heading font-semibold">{valid ? (projectName.trim() || 'Fence installation scope') : 'Complete the core job facts'}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{valid ? 'Copy this into your proposal or use it as the review baseline for the saved estimate.' : 'Add footage, material and height to generate the structured scope.'}</p>
              </div>
              <div className="p-5 sm:p-6">
                {valid ? <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-foreground">{scopeText}</pre> : <div className="flex min-h-64 items-center justify-center rounded-xl border border-dashed border-border bg-muted/20 p-8 text-center"><div><ApperIcon name="FileText" size={28} className="mx-auto text-muted-foreground"/><p className="mt-3 text-sm font-semibold">Your scope preview will appear here.</p><p className="mt-1 text-xs text-muted-foreground">No customer data is saved from this public tool.</p></div></div>}
              </div>
            </section>

            <div className="flex flex-wrap gap-3">
              <Button type="button" onClick={copyScope} disabled={!valid}><ApperIcon name={copied?'Check':'Copy'} size={16}/>{copied?'Copied':'Copy scope text'}</Button>
              {valid?<Button variant="outline" asChild><a href={'data:text/plain;charset=utf-8,'+encodeURIComponent(scopeText)} download="picketops-fence-scope.txt"><ApperIcon name="Download" size={16}/>Download scope</a></Button>:<Button variant="outline" disabled><ApperIcon name="Download" size={16}/>Download scope</Button>}
              {valid&&<Button variant="outline" asChild><Link to={'/fence-labor-crew-planner?feet='+encodeURIComponent(feet)+'&job='+encodeURIComponent('Fence scope builder')}><ApperIcon name="Users" size={16}/>Plan crew labor</Link></Button>}
              {valid&&<Button variant="outline" asChild><Link to={'/fence-proposal-option-builder?project='+encodeURIComponent(projectName.trim())+'&material='+encodeURIComponent(material)+'&feet='+encodeURIComponent(feet)+'&gates='+encodeURIComponent(gates)+'&tearout='+encodeURIComponent(tearout)}><ApperIcon name="Columns3" size={16}/>Build proposal options</Link></Button>}
              <Button variant="outline" asChild><Link to="/fence-estimate-checklist">Review the scope checklist <ApperIcon name="ArrowRight" size={16}/></Link></Button>
              <Button variant="outline" asChild><Link to={workspaceTarget}>{isAuthenticated?'Open saved estimates':'Create account'}</Link></Button>
            </div>
          </div>
        </section>

        <section aria-labelledby="scope-faq-heading" className="max-w-4xl mx-auto">
          <h2 id="scope-faq-heading" className="text-2xl font-heading font-semibold">Fence estimate template questions</h2>
          <div className="mt-5 divide-y rounded-xl border border-border bg-card px-5">
            {FAQ.map(([question, answer]) => <details key={question} className="py-4"><summary className="cursor-pointer rounded-md font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{question}</summary><p className="mt-3 text-sm leading-relaxed text-muted-foreground">{answer}</p></details>)}
          </div>
        </section>
      </main>
    </div>
  );
}

export const route = { path: '/fence-estimate-template', layout: 'public' };
